import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import {
  Activity,
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Copy,
  FileText,
  History,
  Lightbulb,
  Link as LinkIcon,
  PlayCircle,
  Search,
  Sparkles,
  Target,
  Trash2,
  Wand2,
} from 'lucide-react';
import { api } from '../../api/client';
import EmptyState from '../../components/EmptyState';
import AIBadge from '../../components/AIBadge';
import InfoTip from '../../components/InfoTip';
import ErrorBanner from '../../components/ErrorBanner';
import { useFeature, upgradeHintFor } from '../../lib/features';
import { Lock } from 'lucide-react';
import { SkeletonHero, SkeletonList } from '../../components/Skeleton';
import { toast } from '../../lib/toast';
import { formatDate, formatRelative } from '../../lib/format';

const PRIORITY_META = {
  high: { label: 'High priority', class: 'pill bg-rose-50 text-rose-700' },
  medium: { label: 'Medium', class: 'pill bg-amber-50 text-amber-700' },
  low: { label: 'Low', class: 'pill bg-gray-100 text-gray-700' },
};

const INTENT_META = {
  informational: 'Informational',
  commercial: 'Commercial',
  navigational: 'Navigational',
  transactional: 'Transactional',
};

export default function GapsTab({ domain }) {
  const qc = useQueryClient();

  const latestQuery = useQuery({
    queryKey: ['gap-latest', domain._id],
    queryFn: () =>
      api.get(`/api/domains/${domain._id}/content-gaps/latest`).then((r) => r.data.analysis),
  });

  const historyQuery = useQuery({
    queryKey: ['gap-history', domain._id],
    queryFn: () =>
      api.get(`/api/domains/${domain._id}/content-gaps`).then((r) => r.data.analyses),
  });

  const runMutation = useMutation({
    mutationFn: () =>
      api.post(`/api/domains/${domain._id}/content-gaps`).then((r) => r.data.analysis),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gap-latest', domain._id] });
      qc.invalidateQueries({ queryKey: ['gap-history', domain._id] });
    },
  });

  const analysis = latestQuery.data;
  const history = historyQuery.data || [];

  if (latestQuery.isLoading) {
    return (
      <div className="space-y-6">
        <SkeletonHero />
        <SkeletonList rows={6} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <GapHero
        domain={domain}
        analysis={analysis}
        runMutation={runMutation}
      />

      {runMutation.isError && (
        <GapAnalysisError
          error={runMutation.error}
          domain={domain}
          onDismiss={() => runMutation.reset()}
        />
      )}

      {!analysis ? (
        <EmptyState
          icon={Sparkles}
          title="No gap analysis yet"
          body="AI scans your top pages, infers what your business does, and lists 10–15 specific questions users would ask AI engines that you might not be answering yet."
          action={
            <button
              className="btn-primary"
              onClick={() => runMutation.mutate()}
              disabled={runMutation.isPending}
            >
              {runMutation.isPending ? (
                <>
                  <Activity className="w-4 h-4 animate-pulse" /> Analyzing (30–60s)…
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4" /> Run gap analysis
                </>
              )}
            </button>
          }
        />
      ) : (
        <>
          <AnalysisSummary analysis={analysis} />
          <GapList analysis={analysis} domain={domain} />
        </>
      )}

      {history.length > 1 && <HistoryCard history={history} currentId={analysis?._id} domain={domain} />}
    </div>
  );
}

function GapHero({ domain, analysis, runMutation }) {
  const gapsCount = analysis?.gaps?.length || 0;
  const highCount = analysis?.gaps?.filter((g) => g.priority === 'high').length || 0;

  return (
    <div className="relative rounded-2xl overflow-hidden bg-ink-gradient text-white p-6 sm:p-8 shadow-xl">
      <div className="absolute inset-0 opacity-30 bg-brand-radial" />
      <div className="absolute inset-0 bg-grid opacity-20" />
      <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-brand-600 blur-3xl opacity-30" />
      <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-white/60 mb-1 flex items-center gap-1.5">
            <Search className="w-3 h-3" /> Content gap analyzer
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold">
            {analysis ? `${gapsCount} gaps found` : 'Find what AI is asking — that you haven’t answered'}
          </h2>
          {analysis ? (
            <div className="text-sm text-white/70 mt-1">
              {analysis.business_type ? `${analysis.business_type} · ` : ''}
              {analysis.industry || domain.domain}
              {highCount > 0 && (
                <>
                  {' · '}
                  <span className="font-semibold text-rose-200">{highCount} high priority</span>
                </>
              )}
            </div>
          ) : (
            <p className="text-sm text-white/70 mt-1 max-w-xl">
              AI scans your top pages, infers what your business does, and surfaces specific questions users would ask AI engines that your site may not yet cover.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {analysis && (
            <span className="pill bg-white/20 text-white backdrop-blur">
              <CheckCircle2 className="w-3 h-3" /> Updated {formatRelative(analysis.createdAt)}
            </span>
          )}
          <button
            onClick={() => runMutation.mutate()}
            disabled={runMutation.isPending}
            className="inline-flex items-center gap-2 rounded-lg bg-white text-ink-900 px-4 py-2 text-sm font-semibold hover:bg-white/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {runMutation.isPending ? (
              <>
                <Activity className="w-4 h-4 animate-pulse" /> Analyzing…
              </>
            ) : analysis ? (
              <>
                <Wand2 className="w-4 h-4" /> Re-run analysis
              </>
            ) : (
              <>
                <PlayCircle className="w-4 h-4" /> Run analysis
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function GapAnalysisError({ error, domain, onDismiss }) {
  const message = error?.response?.data?.error || error?.message || 'Analysis failed';
  const needsKey = /anthropic api key/i.test(message);
  return (
    <ErrorBanner
      title="Gap analysis failed"
      body={
        <>
          {message}
          {needsKey && (
            <div className="mt-2">
              <Link to={`/?d=${domain._id}#settings`} className="underline font-medium">
                Open Settings → Anthropic API key
              </Link>{' '}
              to add yours.
            </div>
          )}
        </>
      }
      onDismiss={onDismiss}
    />
  );
}

function AnalysisSummary({ analysis }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <SummaryTile
        icon={Target}
        label="Gaps found"
        value={analysis.gaps?.length || 0}
      />
      <SummaryTile
        icon={Sparkles}
        label="High priority"
        value={analysis.gaps?.filter((g) => g.priority === 'high').length || 0}
        tone="rose"
      />
      <SummaryTile
        icon={FileText}
        label="Pages analyzed"
        value={analysis.pages_analyzed || 0}
      />
      <SummaryTile
        icon={History}
        label="Estimated cost"
        value={
          analysis.estimated_cost != null
            ? `$${Number(analysis.estimated_cost).toFixed(4)}`
            : '—'
        }
      />
    </div>
  );
}

function SummaryTile({ icon: Icon, label, value, tone = 'brand' }) {
  const toneCls = {
    brand: 'from-brand-50 to-brand-100/40 text-brand-700',
    rose: 'from-rose-50 to-rose-100/40 text-rose-600',
  }[tone];
  return (
    <div className="card-compact relative overflow-hidden">
      <div className={clsx('absolute -top-8 -right-8 h-24 w-24 rounded-full bg-gradient-to-br opacity-60 blur-2xl pointer-events-none', toneCls)} />
      <div className="relative flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">
            {label}
          </div>
          <div className="text-2xl font-bold text-ink-900 mt-0.5">{value}</div>
        </div>
        <span className={clsx('flex-none h-9 w-9 rounded-lg flex items-center justify-center bg-gradient-to-br', toneCls)}>
          <Icon className="w-4 h-4" />
        </span>
      </div>
    </div>
  );
}

function GapList({ analysis, domain }) {
  const gaps = analysis.gaps || [];
  if (!gaps.length) {
    return (
      <EmptyState
        icon={CheckCircle2}
        title="No gaps identified"
        body="The AI couldn't find unanswered questions for this site. Re-run after publishing more pages, or check that the homepage at least is reachable."
      />
    );
  }

  // High priority first, then medium, then low.
  const order = { high: 0, medium: 1, low: 2 };
  const sorted = [...gaps].sort((a, b) => (order[a.priority] || 9) - (order[b.priority] || 9));

  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <Lightbulb className="w-4 h-4" />
          </span>
          Content gaps
        </div>
        <span className="text-xs text-ink-400">Sorted by priority</span>
      </div>
      <ul className="divide-y divide-gray-50">
        {sorted.map((g) => (
          <GapRow key={g._id} gap={g} analysisId={analysis._id} domain={domain} />
        ))}
      </ul>
    </div>
  );
}

function GapRow({ gap, analysisId, domain }) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();

  const outlineMutation = useMutation({
    mutationFn: () =>
      api
        .post(
          `/api/domains/${domain._id}/content-gaps/${analysisId}/gaps/${gap._id}/outline`,
        )
        .then((r) => r.data.gap),
    onSuccess: () => {
      // Invalidate both latest + the specific analysis if cached
      qc.invalidateQueries({ queryKey: ['gap-latest', domain._id] });
    },
  });

  const priorityMeta = PRIORITY_META[gap.priority] || PRIORITY_META.medium;

  return (
    <li className="py-4 first:pt-0 last:pb-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={priorityMeta.class}>{priorityMeta.label}</span>
            <span className="pill-muted">{INTENT_META[gap.intent] || gap.intent}</span>
          </div>
          <div className="font-semibold text-ink-900 mb-1.5">{gap.question}</div>
          {gap.rationale && (
            <p className="text-sm text-ink-700 mb-2 leading-relaxed">{gap.rationale}</p>
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-400">
            {gap.suggested_title && (
              <span className="flex items-center gap-1.5">
                <FileText className="w-3 h-3" />
                <span className="font-medium text-ink-700">{gap.suggested_title}</span>
              </span>
            )}
            {gap.suggested_url && (
              <span className="flex items-center gap-1.5 font-mono">
                <LinkIcon className="w-3 h-3" /> {gap.suggested_url}
              </span>
            )}
          </div>
        </div>
        <div className="flex-none flex flex-col items-end gap-2">
          <BriefButton
            hasOutline={!!gap.outline}
            open={open}
            setOpen={setOpen}
            onGenerate={() => outlineMutation.mutate()}
            pending={outlineMutation.isPending}
          />
        </div>
      </div>

      {outlineMutation.isError && (
        <div className="mt-3">
          <ErrorBanner
            body={outlineMutation.error?.response?.data?.error || 'Outline generation failed.'}
            onDismiss={() => outlineMutation.reset()}
          />
        </div>
      )}

      {gap.outline && open && <OutlineView outline={gap.outline} />}
    </li>
  );
}

function OutlineView({ outline }) {
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    const md = outlineToMarkdown(outline);
    await navigator.clipboard.writeText(md);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="mt-4 rounded-xl border border-brand-100 bg-gradient-to-br from-brand-50/60 via-white to-cyan-500/5 p-4 space-y-4 shadow-sm ring-1 ring-brand-100/40">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-brand-600">
              Content brief
            </div>
            <AIBadge label="AI-generated" />
            <InfoTip
              title="What is this?"
              body="A ready-to-write outline: target word count, primary keyword, section H2s/H3s, an Answer Capsule the AI engines can lift verbatim, and 3–5 FAQ questions. Hand it to a writer or paste it into your editor."
            />
          </div>
          <h4 className="text-lg font-semibold text-ink-900">{outline.title}</h4>
          {outline.metaDescription && (
            <p className="text-sm text-ink-700 mt-1 leading-relaxed italic">
              {outline.metaDescription}
            </p>
          )}
        </div>
        <button className="btn-secondary" onClick={onCopy}>
          {copied ? (
            <>
              <CheckCircle2 className="w-4 h-4" /> Copied
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" /> Copy brief
            </>
          )}
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <BriefStat label="Word count" value={outline.wordCount} />
        <BriefStat label="Primary keyword" value={outline.primaryKeyword} />
        <BriefStat
          label="Sections"
          value={outline.sections?.length || 0}
        />
        <BriefStat label="FAQs" value={outline.faqSuggestions?.length || 0} />
      </div>

      {outline.answerCapsule && (
        <div className="rounded-lg bg-white border border-brand-100 p-3">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-brand-600 mb-1">
            Answer capsule
          </div>
          <p className="text-sm text-ink-800 leading-relaxed">{outline.answerCapsule}</p>
        </div>
      )}

      {outline.sections?.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-ink-400 mb-2">
            Sections
          </div>
          <ol className="space-y-2.5">
            {outline.sections.map((s, i) => (
              <li
                key={i}
                className="rounded-lg bg-white border border-gray-100 p-3"
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="flex-none h-5 w-5 rounded-full bg-brand-50 text-brand-700 text-[10px] font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                    <span className="font-semibold text-ink-900 truncate">
                      {s.heading}
                    </span>
                  </div>
                  {s.wordCount && (
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-400 flex-none">
                      ~{s.wordCount}w
                    </span>
                  )}
                </div>
                {s.purpose && (
                  <p className="text-xs text-ink-400 leading-relaxed pl-7 mb-1.5">
                    {s.purpose}
                  </p>
                )}
                {s.keyPoints?.length > 0 && (
                  <ul className="pl-7 text-xs text-ink-700 list-disc list-inside space-y-0.5">
                    {s.keyPoints.map((p, j) => (
                      <li key={j}>{p}</li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}

      {outline.faqSuggestions?.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-ink-400 mb-2">
            Suggested FAQs
          </div>
          <ul className="space-y-2">
            {outline.faqSuggestions.map((f, i) => (
              <li
                key={i}
                className="rounded-lg bg-white border border-gray-100 p-3 text-sm"
              >
                <div className="font-medium text-ink-900">{f.question}</div>
                <div className="text-ink-400 mt-1">{f.shortAnswer}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {outline.secondaryKeywords?.length > 0 && (
        <div className="text-xs">
          <span className="font-semibold uppercase tracking-wide text-ink-400 mr-2">
            Secondary keywords:
          </span>
          {outline.secondaryKeywords.map((k, i) => (
            <span
              key={i}
              className="inline-block mr-1.5 mb-1 px-2 py-0.5 rounded bg-white border border-gray-200 text-ink-700"
            >
              {k}
            </span>
          ))}
        </div>
      )}

      {outline.internalLinkOpportunities?.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-ink-400 mb-2">
            Internal link opportunities
          </div>
          <ul className="space-y-1.5 text-xs">
            {outline.internalLinkOpportunities.map((entry, i) => {
              // Legacy entries are bare strings; new ones are { url, anchorText }.
              const link = typeof entry === 'string' ? null : entry?.url || null;
              const anchor =
                typeof entry === 'string'
                  ? entry
                  : entry?.anchorText || entry?.text || link || '(no anchor)';
              return (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-brand-500 mt-0.5">→</span>
                  <div className="min-w-0">
                    <span className="text-ink-800">{anchor}</span>
                    {link && (
                      <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block font-mono text-[10px] text-ink-400 truncate hover:text-brand-600 hover:underline"
                      >
                        {link}
                      </a>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function BriefButton({ hasOutline, open, setOpen, onGenerate, pending }) {
  const unlocked = useFeature('contentBriefs');

  if (!unlocked && !hasOutline) {
    return (
      <button
        type="button"
        className="btn-secondary opacity-70 cursor-not-allowed"
        title={`AI-generated briefs are a ${upgradeHintFor('contentBriefs')} feature`}
        onClick={(e) => e.preventDefault()}
      >
        <Lock className="w-4 h-4" /> Brief (Pro)
      </button>
    );
  }
  return (
    <button
      className="btn-secondary"
      onClick={() => {
        setOpen(true);
        if (!hasOutline) onGenerate();
      }}
      disabled={pending}
    >
      {pending ? (
        <>
          <Activity className="w-4 h-4 animate-pulse" /> Generating…
        </>
      ) : hasOutline ? (
        <>
          <FileText className="w-4 h-4" /> View brief
          {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </>
      ) : (
        <>
          <Wand2 className="w-4 h-4" /> Create brief <ArrowRight className="w-3 h-3" />
        </>
      )}
    </button>
  );
}

function BriefStat({ label, value }) {
  return (
    <div className="rounded-lg bg-white border border-gray-100 p-2.5">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-400">
        {label}
      </div>
      <div className="text-sm font-semibold text-ink-900 truncate">{value || '—'}</div>
    </div>
  );
}

function HistoryCard({ history, currentId }) {
  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <History className="w-4 h-4" />
          </span>
          Previous analyses
        </div>
        <span className="text-xs text-ink-400">Last {Math.min(history.length, 10)}</span>
      </div>
      <ul className="divide-y divide-gray-50">
        {history.slice(0, 10).map((a) => (
          <li
            key={a._id}
            className="py-2.5 flex items-center justify-between gap-3 first:pt-0 last:pb-0 text-sm"
          >
            <div className="flex items-center gap-3 min-w-0">
              <span
                className={clsx(
                  'flex-none h-8 w-8 rounded-lg flex items-center justify-center font-bold text-xs',
                  a.status === 'failed'
                    ? 'bg-rose-50 text-rose-600'
                    : 'bg-brand-50 text-brand-700',
                )}
              >
                {a.status === 'failed' ? '!' : a.gaps?.length || 0}
              </span>
              <div className="min-w-0">
                <div className="text-ink-800">
                  {formatDate(a.createdAt)}{' '}
                  {a._id === currentId && <span className="pill-brand ml-1">Current</span>}
                </div>
                <div className="text-xs text-ink-400 truncate">
                  {a.status === 'failed'
                    ? a.error || 'Analysis failed'
                    : `${a.pages_analyzed || 0} pages · ${a.business_type || ''}`}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function outlineToMarkdown(o) {
  const lines = [];
  lines.push(`# ${o.title}`);
  if (o.metaDescription) lines.push(`> ${o.metaDescription}`);
  lines.push('');
  if (o.wordCount) lines.push(`**Target word count:** ${o.wordCount}`);
  if (o.primaryKeyword) lines.push(`**Primary keyword:** ${o.primaryKeyword}`);
  if (o.secondaryKeywords?.length) {
    lines.push(`**Secondary keywords:** ${o.secondaryKeywords.join(', ')}`);
  }
  if (o.answerCapsule) {
    lines.push('');
    lines.push('## Answer capsule');
    lines.push(o.answerCapsule);
  }
  if (o.sections?.length) {
    lines.push('');
    lines.push('## Outline');
    o.sections.forEach((s, i) => {
      lines.push(`\n### ${i + 1}. ${s.heading}${s.wordCount ? `  _(~${s.wordCount}w)_` : ''}`);
      if (s.purpose) lines.push(`_${s.purpose}_`);
      if (s.keyPoints?.length) {
        s.keyPoints.forEach((p) => lines.push(`- ${p}`));
      }
    });
  }
  if (o.faqSuggestions?.length) {
    lines.push('');
    lines.push('## Suggested FAQs');
    o.faqSuggestions.forEach((f) => {
      lines.push(`\n**${f.question}**`);
      lines.push(f.shortAnswer);
    });
  }
  if (o.internalLinkOpportunities?.length) {
    lines.push('');
    lines.push('## Internal link opportunities');
    o.internalLinkOpportunities.forEach((l) => {
      if (typeof l === 'string') return lines.push(`- ${l}`);
      const anchor = l.anchorText || l.text || '(no anchor)';
      const url = l.url ? ` — ${l.url}` : '';
      lines.push(`- ${anchor}${url}`);
    });
  }
  return lines.join('\n');
}
